import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import type { IActivitySession } from '@/activity-orchestration/types';

/** 挂件会话只保存可见性与来源，活动数据仍由 ActivityStore 提供。 */

/** 同屏挂件数量上限。 */
export const DEFAULT_MAX_WIDGETS = 3;

interface IActivitySessionState {
    /** activityId -> IActivitySession */
    sessions: Record<string, IActivitySession>;
    /** 同屏挂件上限（服务端下发则覆盖，缺失兜底默认，§6.5） */
    maxWidgets: number;
}

interface IActivitySessionActions {
    /** 写入 / 更新挂件会话（源 1 / 源 2 共用，按 activityId upsert，不覆盖其它活动） */
    upsertSession: (session: IActivitySession) => void;
    /** 移除某活动的挂件会话（一般无需，过期由渲染时按 ActivityStore 过滤兜底） */
    removeSession: (activityId: string) => void;
    /** 设置同屏挂件上限 */
    setMaxWidgets: (maxWidgets: number) => void;
    /** 硬导航 / 重置 */
    reset: () => void;
}

const initialState: IActivitySessionState = {
    sessions: {},
    maxWidgets: DEFAULT_MAX_WIDGETS,
};

export const useActivitySessionStore = create<IActivitySessionState & IActivitySessionActions>()(
    immer(set => ({
        ...initialState,

        upsertSession: session =>
            set(state => {
                const prev = state.sessions[session.activityId];
                state.sessions[session.activityId] = {
                    ...prev,
                    ...session,
                    // 保持首次写入时间，避免同优先级活动切页后重排。
                    createdAt: prev?.createdAt ?? session.createdAt ?? Date.now(),
                };
            }),

        removeSession: activityId =>
            set(state => {
                delete state.sessions[activityId];
            }),

        setMaxWidgets: maxWidgets =>
            set(state => {
                state.maxWidgets = maxWidgets;
            }),

        reset: () => set(() => ({ ...initialState })),
    }))
);
